import React, { useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { canManageRequests } from 'types/resolution';
import { isShipaidAdmin } from 'utils';
import {
  getRequestReturnTo,
  withRequestReturnTo,
} from 'utils/requestNavigation';
import {
  REPORT_FILTER_KEYS,
  RequestReportPreferences,
  readRequestReportPreferences,
} from 'utils/requestReportNavigation';

export function RequestsWorkspace() {
  const { storeId } = useStore();
  const { role, user } = useUser();
  const location = useLocation();
  const pathname = location.pathname.replace(/\/+$/, '').toLowerCase();
  const navigate = useNavigate();
  const previousStore = useRef(storeId);
  const listLocations = useRef({
    storeId,
    requests: '/requests',
    orders: '/orders',
  });
  const changingStore = previousStore.current !== storeId;
  if (listLocations.current.storeId !== storeId)
    listLocations.current = {
      storeId,
      requests: '/requests',
      orders: '/orders',
    };
  const reportDates = useRef<
    RequestReportPreferences & { storeId: typeof storeId }
  >({
    storeId,
    ...readRequestReportPreferences(storeId),
  });
  if (reportDates.current.storeId !== storeId)
    reportDates.current = { storeId, ...readRequestReportPreferences(storeId) };
  useEffect(() => {
    if (previousStore.current !== storeId) {
      previousStore.current = storeId;
      navigate(location.pathname, { replace: true });
    }
  }, [storeId, location.pathname, navigate]);

  if (!storeId) return <p className="p-6">Choose a store to view requests.</p>;
  if (changingStore) return null;
  const isOrders = pathname === '/orders';
  if (!canManageRequests(role, isShipaidAdmin(user)))
    return isOrders ? (
      <Outlet key={storeId} />
    ) : (
      <p className="p-6">
        An Admin or Manager role is required to view this workspace.
      </p>
    );

  const isQueue = pathname === '/requests';
  const isInspectionQueue = pathname === '/requests/inspections';
  const creatingIssue = pathname === '/requests/new-issue';
  const origin = getRequestReturnTo(location.search, undefined, '');
  if (isOrders) listLocations.current.orders = `${pathname}${location.search}`;
  else if (origin.split('?')[0] === '/orders')
    listLocations.current.orders = origin;
  const queueOrigin = isQueue
    ? `${pathname}${location.search}`
    : origin.split('?')[0] === '/requests'
    ? origin
    : listLocations.current.requests;
  const queueUrl = new URL(queueOrigin, 'https://shipaid.local');
  const hasReturnTo = queueUrl.searchParams.has('returnTo');
  queueUrl.searchParams.delete('returnTo');
  // Queue filters identify the view; older navigation origins must not nest on each report drilldown.
  const queue = hasReturnTo
    ? `${queueUrl.pathname}${queueUrl.search}${queueUrl.hash}`
    : queueOrigin;
  listLocations.current.requests = queue;
  const params = new URLSearchParams(location.search);
  for (const key of ['startDate', 'endDate'] as const) {
    const requested = params.get(key);
    if (requested && /^\d{4}-\d{2}-\d{2}$/.test(requested))
      reportDates.current[key] = requested;
  }
  for (const key of REPORT_FILTER_KEYS) {
    if (pathname === '/requests/reports') {
      const value = params.get(key);
      if (value && value.length <= 250) reportDates.current[key] = value;
      else delete reportDates.current[key];
    }
  }
  try {
    const { storeId: _storeId, ...preferences } = reportDates.current;
    sessionStorage.setItem(
      `request-report-dates:${storeId}`,
      JSON.stringify(preferences),
    );
  } catch {
    /* Report preferences are optional when browser storage is unavailable. */
  }
  if (['/requests/reports', '/requests/billing'].includes(pathname))
    return <Outlet key={storeId} />;

  const tabs = [
    {
      name: 'Requests',
      to: queue,
      active: !isOrders && !isInspectionQueue,
    },
    {
      name: 'Inspections',
      to: '/requests/inspections',
      active: isInspectionQueue,
    },
    {
      name: 'All Orders',
      to: listLocations.current.orders,
      active: isOrders,
    },
  ];

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Requests</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage issues, returns, exchanges, order edits, and orders.
            </p>
          </div>
          {!creatingIssue && (
            <Link
              className="inline-flex items-center rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              to={withRequestReturnTo(
                '/requests/new-issue',
                isOrders ? listLocations.current.orders : queue,
              )}
            >
              New request
            </Link>
          )}
        </div>
        <nav
          aria-label="Requests workspace"
          className="mb-6 flex gap-6 border-b border-gray-200"
        >
          {tabs.map((tab) => (
            <Link
              key={tab.name}
              to={tab.to}
              aria-current={tab.active ? 'page' : undefined}
              className={classNames(
                'border-b-2 px-1 pb-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500',
                tab.active
                  ? 'border-blue-700 text-blue-700'
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900',
              )}
            >
              {tab.name}
            </Link>
          ))}
        </nav>
      </div>
      <Outlet key={storeId} />
    </>
  );
}
